import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_calendar.scss'

import { Props, SizeNS } from '../utils/types'
import addSubcomponents from '../utils/addSubcomponents'
import Box, {
  BoxProps,
  FlexAlignNS,
  BoxDisplayNS,
  FlexJustifyNS,
} from '../layouts/Box'
import Flex from '../layouts/Flex'
import Pagination from './Pagination'
import Select from './Select'
import {
  addMonths,
  format,
  getDate,
  getDates,
  getMonth,
  getRows,
  getYear,
  formatMonth,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinRange,
  normalizeNewDate,
  subMonths,
} from './CalendarHelpers'

interface CalendarProps extends BodyProps {
  onNavigate: (date: Date) => void
}

interface BodyProps extends Props {
  displayDate?: Date
  disabledDate?: (date: Date) => boolean
  onSelect: (day: Date) => void
  selectedEnd?: Date
  selectedStart?: Date
}

interface DayProps extends Props {
  disabled?: boolean
  isCurrentMonth?: boolean
  isRangeEnd?: boolean
  isRangeStart?: boolean
  isToday?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  selected?: boolean
}

interface YearSelectProps extends Props {
  date: Date
  year: number
  onSelect: (date: Date) => void
}

interface DayProps extends Props {
  disabled?: boolean
  isCurrentMonth?: boolean
  isRangeEnd?: boolean
  isRangeStart?: boolean
  isToday?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  selected?: boolean
}

interface MonthSelectProps extends Props {
  date: Date
  month: number
  onSelect: (date: Date) => void
}

interface MonthOrYearSelectProps extends Props {
  label: string
  onSelect: (selection: number) => any
}

interface NextPrevProps extends Props {
  onClick: () => void
}

interface YearSelectProps extends Props {
  date: Date
  year: number
  onSelect: (date: Date) => void
}

const cx = bind(styles)

export const Body = ({
  displayDate = new Date(),
  disabledDate,
  onSelect = () => {},
  selectedEnd,
  selectedStart,
  ...rest
}: BodyProps) => {
  const dates = getDates(displayDate)

  return (
    <>
      <Weekdays />
      {getRows(dates).map(row => (
        <Week key={`week-${format(row[0], 'YYYY-MMMM-DDDD')}`}>
          {row.map(day => (
            <Day
              disabled={disabledDate && disabledDate(day)}
              isCurrentMonth={isSameMonth(day, displayDate)}
              isRangeEnd={selectedEnd && isSameDay(day, selectedEnd)}
              isRangeStart={selectedStart && isSameDay(day, selectedStart)}
              isToday={isToday(day)}
              key={`day-${format(day, 'YYYY-MMMM-DDDD')}`}
              onClick={() => onSelect(day)}
              selected={isWithinRange(day, selectedStart, selectedEnd)}
              {...rest}
            >
              {format(day, 'D')}
            </Day>
          ))}
        </Week>
      ))}
    </>
  )
}

export const Container = React.forwardRef<HTMLDivElement, Props>(
  ({ className, ...rest }, ref) => (
    <Box
      className={cx('core-calendar', className)}
      paddingBottom={SizeNS.md}
      paddingLeft={SizeNS.md}
      paddingRight={SizeNS.md}
      paddingTop={SizeNS.lg}
      ref={ref}
      {...rest}
    />
  )
)

export const Day = React.forwardRef<HTMLDivElement, DayProps>(
  (
    {
      className,
      disabled = false,
      isCurrentMonth = false,
      isRangeEnd = false,
      isRangeStart = false,
      isToday = false,
      selected = false,
      ...rest
    },
    ref
  ) => (
    <Box
      alignItems={FlexAlignNS.center}
      className={cx(
        'core-calendar__day',
        {
          ['core-calendar__day--disabled']: disabled,
          ['core-calendar__day--other-month']: !isCurrentMonth,
          ['core-calendar__day--selected']: selected,
          ['core-calendar__day--range-bookend']: isRangeStart || isRangeEnd,
          ['core-calendar__day--range-end']: isRangeEnd,
          ['core-calendar__day--range-start']: isRangeStart,
          ['core-calendar__day--today']: isToday,
        },
        className
      )}
      display={BoxDisplayNS.flexRow}
      justifyContent={FlexJustifyNS.center}
      ref={ref}
      {...rest}
    />
  )
)

export const MonthSelect = React.forwardRef<HTMLDivElement, MonthSelectProps>(
  ({ className, date, month, onSelect, ...props }, ref) => {
    const updateDate = (month: number) =>
      normalizeNewDate(getYear(date), month, getDate(date))

    return (
      <MonthOrYearSelect
        {...props}
        className={cx('core-calendar__month')}
        label={formatMonth(month)}
        onSelect={selection => onSelect(updateDate(selection))}
        ref={ref}
      >
        {Array.from({ length: 12 }, (_, i: number) => (
          <Select.Option value={i} selected={i === month} key={formatMonth(i)}>
            {formatMonth(i)}
          </Select.Option>
        ))}
      </MonthOrYearSelect>
    )
  }
)

const MonthOrYearSelect = React.forwardRef<
  HTMLDivElement,
  MonthOrYearSelectProps
>(({ className, children, label, onSelect, ...props }, ref) => {
  return (
    <Box
      className={cx(className)}
      marginLeft={SizeNS.xs}
      marginRight={SizeNS.xs}
      ref={ref}
    >
      <Select
        {...props}
        block
        className={className}
        label={label}
        onSelect={selection => {
          onSelect(selection.item)
        }}
      >
        {children}
      </Select>
    </Box>
  )
})

export const Navigation = React.forwardRef<HTMLDivElement, BoxProps>(
  ({ className, ...rest }, ref) => (
    <Box
      alignItems={FlexAlignNS.center}
      className={cx(className)}
      display={BoxDisplayNS.flexRow}
      marginBottom={SizeNS.md}
      ref={ref}
      {...rest}
    />
  )
)

export const Next = React.forwardRef<HTMLButtonElement, NextPrevProps>(
  ({ className, ...props }, ref) => (
    <Pagination.Next
      {...props}
      className={cx('core-calendar__next', className)}
      ref={ref}
    />
  )
)

export const Prev = React.forwardRef<HTMLButtonElement, NextPrevProps>(
  ({ className, ...props }, ref) => (
    <Pagination.Prev
      {...props}
      className={cx('core-calendar__prev', className)}
      ref={ref}
    />
  )
)

export const Week = React.forwardRef<HTMLDivElement, Props>(
  ({ className, ...rest }, ref) => (
    <Flex
      className={cx('core-calendar__week', className)}
      justifyContent={FlexJustifyNS.center}
      ref={ref}
      {...rest}
    />
  )
)

export const Weekdays = React.forwardRef<HTMLDivElement, Props>(
  ({ className, ...rest }, ref) => (
    <Flex
      className={cx(
        'core-calendar__week',
        'core-calendar__week--header',
        className
      )}
      justifyContent={FlexJustifyNS.center}
      ref={ref}
      {...rest}
    >
      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
        <Day isCurrentMonth key={`day-${index}`}>
          {day}
        </Day>
      ))}
    </Flex>
  )
)

export const YearSelect = React.forwardRef<HTMLDivElement, YearSelectProps>(
  ({ className, date, year, onSelect, ...props }, ref) => {
    // get an array of years starting with range [year - 5, year + 5]
    const years = Array.from(new Array(11), (_, i: number) => i + (year - 5))

    const updateDate = (year: number) =>
      normalizeNewDate(year, getMonth(date), getDate(date))

    return (
      <MonthOrYearSelect
        {...props}
        className={cx('core-calendar__year')}
        label={`${year}`}
        onSelect={selection => onSelect(updateDate(selection))}
        ref={ref}
      >
        {years.map(y => (
          <Select.Option value={y} selected={year === y} key={y}>
            {y}
          </Select.Option>
        ))}
      </MonthOrYearSelect>
    )
  }
)

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      className,
      displayDate = new Date(),
      disabledDate = () => false,
      onNavigate,
      onSelect,
      selectedEnd,
      selectedStart,
      ...rest
    },
    ref
  ) => {
    const month = getMonth(displayDate)
    const year = getYear(displayDate)

    return (
      <Container {...rest} ref={ref}>
        <Navigation>
          <Prev onClick={() => onNavigate(subMonths(displayDate, 1))} />
          <MonthSelect date={displayDate} month={month} onSelect={onNavigate} />
          <YearSelect date={displayDate} year={year} onSelect={onNavigate} />
          <Next onClick={() => onNavigate(addMonths(displayDate, 1))} />
        </Navigation>
        <Body
          displayDate={displayDate}
          disabledDate={disabledDate}
          onSelect={onSelect}
          selectedEnd={selectedEnd}
          selectedStart={selectedStart}
        />
      </Container>
    )
  }
)

Calendar.displayName = 'Calendar'

Body.displayName = 'Calendar.Body'

Container.displayName = 'Calendar.Container'

Day.displayName = 'Calendar.Day'

Navigation.displayName = 'Calendar.Navigation'

Next.displayName = 'Calendar.Next'

Prev.displayName = 'Calendar.Prev'

Week.displayName = 'Calendar.Week'

Weekdays.displayName = 'Calendar.Weekdays'

export default addSubcomponents(
  {
    Body,
    Container,
    Day,
    Navigation,
    MonthSelect,
    Next,
    Prev,
    Week,
    Weekdays,
    YearSelect,
  },
  Calendar
)
